import { colourFor, indexAtOrAfter, indexAtOrBefore } from './timeBandOverlay';

/**
 * THE BAND FORMING, bar by bar: the running high and low of what has been revealed inside a pillar.
 *
 * <h2>This is NOT the engine's band, and the distinction is the whole design</h2>
 *
 * `timeBandOverlay` draws the levels the BACKEND computed, from `validFrom` onward, and its own
 * note says why that must not be recomputed here: "a visual audit asks whether the FSM put the
 * line THERE, and a chart that recomputed the extremes in the browser would be answering a
 * different question — it would agree with itself whatever the engine had done."
 *
 * <p>That rule stands and is untouched. What this adds is the other half of the same picture: while
 * the playhead is INSIDE a generation interval, the band's extremes do not exist yet — not in the
 * engine's table and not anywhere — because they are the extremes of bars nobody has seen. So they
 * are drawn as they accumulate, from REVEALED bars only, in a style that cannot be mistaken for a
 * settled level: dashed, half-opacity, labelled `forming`.
 *
 * <pre>
 *   ░░░░░ band in progress ░░░░░            ░░░░ next band ░░░░
 *   ╌╌╌╌ forming high ╌╌╌ (grows)
 *   ╌╌╌╌ forming low  ╌╌╌ (grows)
 *                          ───── Top ─────────── (the ENGINE's, from validFrom)
 *                          ━━━━━ Bottom ━━━━━━━━
 * </pre>
 *
 * <h2>IT CANNOT LEAK, because it only ever reads revealed bars</h2>
 *
 * The high of the first three bars of a band is a fact about three bars the operator is looking at.
 * The high of the whole band is not, until the band ends. Bounding the scan at the playhead is what
 * makes the difference, and it is the same bound every other price-bearing decoration uses.
 *
 * <h2>And it must AGREE with the engine when the band completes</h2>
 *
 * Once the playhead has passed a band's last bar, the forming extremes are the extremes of every
 * bar in it — which is what the engine's `top` and `bottom` are. `formingBands.test.js` asserts
 * that convergence against a shipped band, because a browser-side extreme that disagreed with the
 * engine's would mean one of the two was reading different bars, and it would do so silently.
 */

/** Dashes and half opacity: a level that is still moving must not look like one that has settled. */
export const FORMING_OPACITY = 0.55;
export const FORMING_WIDTH = 1;
export const FORMING_DASH = [3, 3];

/**
 * The running extremes of the revealed bars inside one band.
 *
 * @param band  a `TimeBandDto` — `{category, bandStart, bandEnd, …}`
 * @param dates the chart's full axis
 * @param barData the chart's own `{ohlc}`
 * @param upToIndex the playhead, in axis columns
 * @returns `{high, low, from, to, bars}` or null when nothing inside it has been revealed
 */
export function formingExtremes(band, dates, barData, upToIndex, floorIndex = 0) {
  const axis = Array.isArray(dates) ? dates : [];
  const ohlc = Array.isArray(barData?.ohlc) ? barData.ohlc : [];
  if (!band || axis.length === 0 || ohlc.length === 0) return null;

  // BOUNDED BELOW BY THE SESSION'S FIRST BAR as well as above by the playhead.
  //
  // A session erases the chart and unveils from its own start, so a band straddling that start
  // has bars the operator has not been shown. They are in the PAST rather than the future, so
  // reading them is not a look-ahead leak — but the label says "(N bars)" and a reader who can
  // see three of them should not be told the level rests on twenty.
  // THE -1 IS CHECKED BEFORE THE FLOOR IS APPLIED. `Math.max(-1, floor)` turns "this band is not
  // on the axis at all" into "it starts at the floor", which is a confident wrong answer — and a
  // test caught exactly that on the first version of this clamp.
  const rawFrom = indexAtOrAfter(axis, band.bandStart);
  const last = indexAtOrBefore(axis, band.bandEnd);
  if (rawFrom < 0 || last < 0 || last < rawFrom) return null;

  const floor = Number.isFinite(floorIndex) ? Math.max(0, Math.trunc(floorIndex)) : 0;
  const from = Math.max(rawFrom, floor);
  if (from > last) return null;

  // BOUNDED AT THE PLAYHEAD, which is what makes this honest. The scan also stops at the last real
  // bar: a column in the synthetic future has no price, and reaching into it would read `null` as
  // a number.
  const to = Math.min(last, Math.trunc(upToIndex), ohlc.length - 1);
  if (to < from) return null;

  let high = -Infinity;
  let low = Infinity;
  let bars = 0;
  for (let i = from; i <= to; i++) {
    const bar = ohlc[i];
    // The chart's OHLC is `[open, close, low, high]` — the order ECharts wants, not the order the
    // name suggests. Reading [1] as the high here is the classic way to draw a band off the closes
    // and have it look almost right.
    if (!bar || bar[3] == null || bar[2] == null) continue;
    if (bar[3] > high) high = bar[3];
    if (bar[2] < low) low = bar[2];
    bars += 1;
  }
  if (bars === 0) return null;
  return { high, low, from, to, bars };
}

/**
 * Is this band still in progress as of the playhead?
 *
 * <p>In progress means the playhead has reached its first bar and not yet passed its last. Once it
 * has passed, the engine's own rays take over from `validFrom` and two levels for one band would
 * be two answers to one question.
 */
export function isForming(band, dates, upToIndex) {
  const axis = Array.isArray(dates) ? dates : [];
  if (!band || axis.length === 0) return false;
  const from = indexAtOrAfter(axis, band.bandStart);
  const last = indexAtOrBefore(axis, band.bandEnd);
  if (from < 0 || last < 0 || last < from) return false;
  const at = Math.trunc(upToIndex);
  return at >= from && at <= last;
}

/**
 * The forming levels as `markLine` coordinate pairs, for every band still in progress.
 *
 * <p>Drawn from the band's first revealed bar to the PLAYHEAD rather than to the band's end: the
 * segment is the extent of what is known, and running it to the band's close would put a line over
 * bars nobody has seen — which is the leak this whole mechanism exists to avoid.
 *
 * <p>Two points per level, the same shape the engine's rays and the trade vectors use. A `lines`
 * series is deliberately avoided: it defaults to the GEO coordinate system and on a chart with no
 * geo component it validates, draws nothing, and warns about nothing.
 */
export function formingRays(timeBands, dates, barData, upToIndex, floorIndex = 0) {
  const bands = Array.isArray(timeBands) ? timeBands : [];
  const axis = Array.isArray(dates) ? dates : [];
  if (bands.length === 0 || axis.length === 0) return [];

  const out = [];
  bands.forEach((band) => {
    if (!isForming(band, dates, upToIndex)) return;
    const ex = formingExtremes(band, dates, barData, upToIndex, floorIndex);
    if (!ex) return;
    const colour = colourFor(band.category);

    [['high', ex.high], ['low', ex.low]].forEach(([which, level]) => {
      if (!Number.isFinite(level)) return;
      out.push([
        {
          coord: [axis[ex.from], level],
          lineStyle: {
            color: colour,
            width: FORMING_WIDTH,
            type: FORMING_DASH,
            opacity: FORMING_OPACITY,
          },
          label: {
            show: true,
            // e.g. "PTA forming high 452.10 (3 bars)" -- the count is there because a level from
            // three bars and a level from twenty are different claims, and the only thing that
            // distinguishes them is how much of the band has happened.
            formatter: `${band.category} forming ${which} ${level.toFixed(2)}`
              + ` (${ex.bars} bar${ex.bars === 1 ? '' : 's'})`,
            position: 'insideStartTop',
            color: colour,
            fontSize: 9,
            opacity: FORMING_OPACITY,
          },
        },
        { coord: [axis[ex.to], level] },
      ]);
    });
  });
  return out;
}
